import { Box, Stack, Typography, useTheme } from "@mui/material";
import { FileUploadIcon, UploadIcon } from "components/icons";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";

interface KbImportUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  maxFiles: number;
  currentFileCount: number;
  disabled?: boolean;
}

const ACCEPTED_FILE_TYPES = [
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/pdf", // .pdf
  "text/markdown", //.md
  "text/plain", // .txt
];

const ACCEPTED_EXTENSIONS = [".doc", ".docx", ".pdf", ".md", ".txt"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file

const validateFile = (file: File): { valid: boolean; error?: string } => {
  // Check file extension
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  if (!ACCEPTED_EXTENSIONS.includes(extension)) {
    return {
      valid: false,
      error: `File type ${extension} is not supported. Only .doc, .pdf, .md and .txt formats are allowed.`,
    };
  }

  // Check MIME type (if available)
  if (file.type && !ACCEPTED_FILE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error:
        "Invalid file format. Only .doc, .pdf, .md and .txt formats are allowed.",
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds 10MB limit. ${file.name} is ${(file.size / 1024 / 1024).toFixed(1)}MB.`,
    };
  }

  return { valid: true };
};

export const KbImportUploadZone = ({
  onFilesSelected,
  maxFiles,
  currentFileCount,
  disabled = false,
}: KbImportUploadZoneProps) => {
  const theme = useTheme();
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || disabled) return;

      const filesArray = Array.from(fileList);
      const remainingSlots = maxFiles - currentFileCount;

      if (filesArray.length > remainingSlots) {
        toast.error(
          `Maximum ${maxFiles} files allowed per upload. You can add ${remainingSlots} more file${remainingSlots !== 1 ? "s" : ""}.`,
        );
        return;
      }

      // Validate all files
      const validFiles: File[] = [];
      const errors: string[] = [];

      filesArray.forEach((file) => {
        const validation = validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else if (validation.error) {
          errors.push(validation.error);
        }
      });

      // Show errors
      if (errors.length > 0) {
        errors.forEach((error) => toast.error(error));
      }

      // Add valid files
      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    },
    [maxFiles, currentFileCount, onFilesSelected, disabled],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragActive(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      handleFiles(files);
    },
    [handleFiles, disabled],
  );

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset input value to allow selecting the same file again
      if (e.target) {
        e.target.value = "";
      }
    },
    [handleFiles],
  );

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS.join(",")}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
        aria-label="Upload files to Knowledge Base"
      />
      <Stack
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        sx={{
          border: "1px dashed",
          borderColor: theme.palette.primary.main,
          borderRadius: "4px",
          padding: "16px",
          textAlign: "center",
          backgroundColor: theme.palette.primary.tint,
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.2s ease",
          opacity: disabled ? 0.6 : 1,
          alignItems: "center",
          gap: "8px",
          "&:hover": !disabled
            ? {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.tint,
              }
            : {},
        }}
      >
        <UploadIcon
          sx={{
            fontSize: "24px",
            color: theme.palette.primary.main,
          }}
        />
        <Typography
          variant="body-medium"
          sx={{
            color: theme.palette.lightBg.low,
          }}
        >
          Drag & drop /{" "}
          <Typography
            component="span"
            variant="body-medium"
            sx={{
              color: theme.palette.primary.main,
            }}
          >
            click
          </Typography>{" "}
          to upload files
        </Typography>
      </Stack>
    </Box>
  );
};
